import { useState, useEffect, useCallback } from 'react';
import {
  DashboardFilter,
  AdminDashboardData,
  OfficerDashboardData,
  SupervisorDashboardData,
  VendorDashboardData,
} from '../types/dashboard';
import { AuditLog } from '../types/audit';
import { Complaint } from '../types/complaint';
import { reportsService } from '../services/reports/reportsService';
import { auditService } from '../services/audit/auditService';
import { complaintService } from '../services/complaint/complaintService';
import { getCached, setCached, CACHE_TTL_MS } from '../utils/cache';

function filterKey(filter: DashboardFilter): string {
  const keys = Object.keys(filter).sort() as (keyof DashboardFilter)[];
  return keys.map((k) => `${k}=${filter[k] ?? ''}`).join('&') || 'all';
}

export function useAdminDashboard() {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DashboardFilter>({});

  const fetchDashboard = useCallback(async (force = false) => {
    const cacheKey = `dashboard:admin:${filterKey(filter)}`;
    if (!force) {
      const cached = getCached<AdminDashboardData>(cacheKey);
      if (cached) {
        setData(cached);
        setError(null);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getAdminDashboard(filter);
      if (res.success) {
        setData(res.data);
        setCached(cacheKey, res.data, CACHE_TTL_MS);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, filter, setFilter, refetch: () => fetchDashboard(true) };
}

export function useRecentActivity(limit = 6) {
  const [data, setData] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = useCallback(async (force = false) => {
    const cacheKey = `dashboard:activity:${limit}`;
    if (!force) {
      const cached = getCached<AuditLog[]>(cacheKey);
      if (cached) {
        setData(cached);
        setError(null);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await auditService.getAuditLogs({ limit });
      if (res.success && res.data) {
        setData(res.data.logs);
        setCached(cacheKey, res.data.logs, CACHE_TTL_MS);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load recent activity');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { data, loading, error, refetch: () => fetchActivity(true) };
}

export function useRecentComplaints(limit = 5) {
  const [data, setData] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComplaints = useCallback(async (force = false) => {
    const cacheKey = `dashboard:complaints:${limit}`;
    if (!force) {
      const cached = getCached<Complaint[]>(cacheKey);
      if (cached) {
        setData(cached);
        setError(null);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const res = await complaintService.getComplaints();
      if (res.success && res.data) {
        const recent = [...res.data]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, limit);
        setData(recent);
        setCached(cacheKey, recent, CACHE_TTL_MS);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load recent complaints');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  return { data, loading, error, refetch: () => fetchComplaints(true) };
}

export function useOfficerDashboard() {
  const [data, setData] = useState<OfficerDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DashboardFilter>({});

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getOfficerDashboard(filter);
      if (res.success) {
        setData(res.data);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, filter, setFilter, refetch: fetchDashboard };
}

export function useSupervisorDashboard() {
  const [data, setData] = useState<SupervisorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DashboardFilter>({});

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getSupervisorDashboard(filter);
      if (res.success) {
        setData(res.data);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, filter, setFilter, refetch: fetchDashboard };
}

export function useVendorDashboard(vendorId: string) {
  const [data, setData] = useState<VendorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DashboardFilter>({});

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getVendorDashboard(vendorId, filter);
      if (res.success) {
        setData(res.data);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [vendorId, filter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, filter, setFilter, refetch: fetchDashboard };
}
